import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UINotificationService } from '@bannerflow/ui';
import { GetCreativesHeavyAssetMetadataQueryResponse, HeavyAssetDto } from '@domain/api/generated/sapi';
import { ICreative } from '@domain/creativeset/creative';
import { IVideoValidationState } from '@studio/domain/video-validation';
import { debounceTime, filter, take } from 'rxjs';
import { CreativesetDataService } from '../creativeSet';
import { EnvironmentService } from '../services/environment.service';
import { UserService } from '../user';
import { UserSettingsService } from '../user-settings';
import { HeavyAssetsDataService } from './heavy-assets.data.service';
import { HeavyVideoTrackerService } from './heavy-video-tracker.service';

export const LIMITS_IN_BYTES = {
    base: { bytes: 2_000_000, mb: 2 },
    high: { bytes: 25_000_000, mb: 25 },
    mid: { bytes: 10_000_000, mb: 10 }
};

type HeavyAssetsMetadata = {
    [creativeId: string]: HeavyAssetDto[];
};

@Injectable({
    providedIn: 'root'
})
export class HeavyAssetsService {
    private creativeSetDataService = inject(CreativesetDataService);
    private environmentService = inject(EnvironmentService);
    private heavyAssetsDataService = inject(HeavyAssetsDataService);
    private heavyVideoTrackerService = inject(HeavyVideoTrackerService);
    private uiNotificationService = inject(UINotificationService);
    private userService = inject(UserService);
    private userSettingsService = inject(UserSettingsService);

    private _heavyAssetData = signal<HeavyAssetsMetadata>({});
    heavyAssetsData = this._heavyAssetData.asReadonly();

    private _heavyVideoSizeLimit = signal(LIMITS_IN_BYTES.base);
    heavyVideoSizeLimit = this._heavyVideoSizeLimit.asReadonly();

    private videoValidationWarnings = toSignal(this.userSettingsService.videoValidationWarnings$, {
        initialValue: []
    });

    constructor() {
        this.creativeSetDataService.creativeset$
            .pipe(takeUntilDestroyed(), debounceTime(500))
            .subscribe(creativeSet => {
                if (!creativeSet || this.environmentService.inShowcaseMode) {
                    return;
                }
                this.checkHeavyAssetsFor(creativeSet.creatives);
            });

        this.userService.loaded$
            .pipe(
                takeUntilDestroyed(),
                filter(loaded => !!loaded)
            )
            .subscribe(() => {
                this.initializePermissions();
            });

        effect(() => {
            const heavyAssetData = this.heavyAssetsData();
            if (!Object.keys(heavyAssetData).length) {
                return;
            }

            const creatives = this.creativeSetDataService.creativeset?.creatives ?? [];
            if (!creatives?.length) {
                return;
            }
            const creativeSetId = this.creativeSetDataService.creativeset.id;

            const videoValidationWarnings = untracked(this.videoValidationWarnings);
            if (videoValidationWarnings.some(({ id }) => id === creativeSetId)) {
                return;
            }
            if (!this.heavyVideoTrackerService.isCreativeSetVisited(creativeSetId)) {
                const hasHeavyVideo = this.checkHeavyAssetsInCreativeSet(heavyAssetData, creatives);
                if (hasHeavyVideo) {
                    this.heavyVideoTrackerService.markCreativeSetVisited(creativeSetId);
                }
            }
        });
    }

    clearSuppressedWarnings(): void {
        const creativeSetId = this.creativeSetDataService.creativeset?.id;
        if (!creativeSetId) {
            return;
        }
        const ignoredVideoValidationResults = this.videoValidationWarnings();
        const filteredIgnored = ignoredVideoValidationResults.filter(({ id }) => id !== creativeSetId);

        this.userSettingsService.setIgnoreWarnings('videoValidationWarnings', filteredIgnored);
        this.heavyVideoTrackerService.unmarkCreativeSetVisited(creativeSetId);
    }

    private checkHeavyAssetsInCreativeSet(
        heavyAssetData: HeavyAssetsMetadata,
        creatives: ICreative[]
    ): boolean {
        const heavyVideoSizeLimit = this.heavyVideoSizeLimit();
        const hasHeavyVideo = creatives.some(({ id }) =>
            heavyAssetData[id]?.some(({ contentLength }) => contentLength > heavyVideoSizeLimit.bytes)
        );

        if (!hasHeavyVideo) {
            return false;
        }

        const message = `One or multiple sizes in this creative set contain a heavy video (over ${heavyVideoSizeLimit.mb}MB). Possibility to use these sizes in display campaigns will be limited. <a target='_blank' href="http://support.bannerflow.com/en/articles/6401112-working-with-heavy-videos">Read more here</a>.`;
        this.uiNotificationService.open(
            message,
            { type: 'warning', placement: 'top' },
            'Don’t show this again',
            true
        );
        this.uiNotificationService.notificationRef.instance.onCloseCheckBox
            .pipe(take(1))
            .subscribe(isCheckboxChecked => {
                if (!isCheckboxChecked) {
                    return;
                }
                const creativeSetId = this.creativeSetDataService.creativeset.id;
                const ignoredVideoValidationResults = this.videoValidationWarnings();
                const newIgnoredResults: IVideoValidationState[] = Array.from(
                    new Set([
                        ...ignoredVideoValidationResults.filter(({ id }) => id !== creativeSetId),
                        { id: creativeSetId, hasHeavyVideo: true }
                    ])
                );
                this.userSettingsService.setIgnoreWarnings(
                    'videoValidationWarnings',
                    newIgnoredResults
                );
            });
        return true;
    }

    private checkHeavyAssetsFor(creatives: ICreative[]): void {
        const creativeIds = creatives.map(({ id }) => parseInt(id, 10));
        if (creativeIds.length) {
            this.heavyAssetsDataService.loadHeavyAssetsMetadata(creativeIds).subscribe(metadata => {
                this.updateHeavyAssetData(metadata);
            });
        }
    }

    private updateHeavyAssetData(
        metadata: GetCreativesHeavyAssetMetadataQueryResponse | undefined
    ): void {
        if (!metadata) {
            this._heavyAssetData.set({});
            return;
        }
        this._heavyAssetData.update(heavyAssetData => {
            const newHeavyAssetData = {
                ...heavyAssetData
            };
            for (const { id, heavyAssets } of metadata.creativesHeavyAssetMetadata) {
                newHeavyAssetData[id] = heavyAssets;
            }
            return newHeavyAssetData;
        });
    }

    private async initializePermissions(): Promise<void> {
        const hasAllowHeavyVideos = await this.userService.hasPermission('AllowHeavyVideos');
        if (!hasAllowHeavyVideos) {
            this._heavyVideoSizeLimit.set(LIMITS_IN_BYTES.base);
            return;
        }

        const hasImpactVideos25MbPermission = await this.userService.hasPermission('ImpactVideos25Mb');
        if (hasImpactVideos25MbPermission) {
            this._heavyVideoSizeLimit.set(LIMITS_IN_BYTES.high);
            return;
        }
        const hasImpactVideos10MbPermission = await this.userService.hasPermission('ImpactVideos10Mb');
        if (hasImpactVideos10MbPermission) {
            this._heavyVideoSizeLimit.set(LIMITS_IN_BYTES.mid);
            return;
        }
        this._heavyVideoSizeLimit.set(LIMITS_IN_BYTES.base);
    }
}
